import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import './CPanel.css';

// Same cadence the server checks on, so a dashboard left open on a wall
// doesn't quietly go stale - matches the staff cPanel page.
const REFRESH_MS = 60000;

const METRIC_LABELS = {
  nproc: 'Processes',
  ep: 'Entry Procs',
  pmem: 'Memory',
  io: 'I/O',
  iops: 'IOPS',
  speed: 'CPU'
};
const METRIC_ORDER = ['nproc', 'ep', 'pmem', 'io', 'iops', 'speed'];

const formatWhen = (value) => {
  if (!value) return 'Never';
  const date = new Date(value);
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)} h ago`;
  return date.toLocaleString();
};

// ok / warn / maxed drives both the row's badge and the process cell's
// color - 80% is a heads-up, 100%+ is the "stuck" state Slack alerts on.
const stateFor = (account) => {
  if (account.last_error) return 'error';
  if (!account.nproc_limit) return 'unknown';
  const ratio = account.nproc_usage / account.nproc_limit;
  if (ratio >= 1) return 'maxed';
  if (ratio >= 0.8) return 'warn';
  return 'ok';
};

const formatMetric = (metric) => {
  if (!metric) return '—';
  if (metric.limit !== null && metric.limit !== undefined) return `${metric.usage}/${metric.limit}`;
  if (metric.usage !== null && metric.usage !== undefined) return `${metric.usage}`;
  return '—';
};

function PortalCpanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      setData(await apiClient.get('/api/cpanel/mine'));
      setError('');
    } catch (err) {
      console.error('Error loading cPanel accounts:', err);
      setError(err.message || 'Could not load your resource usage.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const columns = [
    {
      key: 'cpanel_username',
      label: 'Account',
      sortable: true,
      render: (account) => {
        const state = stateFor(account);
        return (
          <div className="cpanel-account">
            <span className={`cpanel-dot cpanel-dot-${state}`} aria-hidden="true"></span>
            <div>
              <div className="cpanel-domain">{account.domain || account.cpanel_username}</div>
              <div className="cpanel-username">
                {account.cpanel_username}
                {account.suspended && <span className="cpanel-suspended-tag">suspended</span>}
              </div>
              {account.last_error && <div className="cpanel-reason">{account.last_error}</div>}
            </div>
          </div>
        );
      }
    },
    {
      key: 'state',
      label: 'Status',
      sortable: true,
      sortValue: (account) => stateFor(account),
      render: (account) => {
        const state = stateFor(account);
        return <span className={`cpanel-badge cpanel-badge-${state}`}>{state}</span>;
      }
    },
    ...METRIC_ORDER.map((key) => ({
      key,
      label: METRIC_LABELS[key],
      sortable: key === 'nproc',
      sortValue: key === 'nproc' ? (account) => (account.nproc_limit ? account.nproc_usage / account.nproc_limit : -1) : undefined,
      render: (account) => {
        const metric = account.usage_snapshot?.[key];
        const text = formatMetric(metric);
        if (key !== 'nproc') return text;
        return <span className={`cpanel-nproc cpanel-nproc-${stateFor(account)}`}>{text}</span>;
      }
    })),
    {
      key: 'last_checked_at',
      label: 'Checked',
      sortable: true,
      render: (account) => formatWhen(account.last_checked_at)
    }
  ];

  const summary = data?.summary;

  return (
    <PortalShell>
      <div className="cpanel-header">
        <div>
          <h1>Resource Usage</h1>
          <p className="cpanel-intro">Your hosting account's resource usage, checked every 5 minutes.</p>
        </div>
      </div>

      {error && <div className="cpanel-error">{error}</div>}

      {summary && (
        <div className="cpanel-summary">
          <div className="cpanel-card">
            <span className="cpanel-card-value">{summary.total}</span>
            <span className="cpanel-card-label">Accounts</span>
          </div>
          <div className={`cpanel-card ${summary.maxed > 0 ? 'cpanel-card-maxed' : ''}`}>
            <span className="cpanel-card-value">{summary.maxed}</span>
            <span className="cpanel-card-label">At process limit</span>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading your resource usage...</p>
      ) : (
        <ResponsiveTable
          columns={columns}
          rows={data?.accounts || []}
          rowKey="cpanel_username"
          emptyMessage="You don't have any hosting accounts with us yet."
        />
      )}
    </PortalShell>
  );
}

export default PortalCpanel;
